import { openModal } from "action/modal/openModal";
import PAButton, { BUTTON_TYPE } from "component/design-system/buttons/PAButton";
import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import FaqAccordion from "container/FaqAccordion";
import { RootState } from "interfaces/generics.type";
import PageContainer from "modules/components/PageContainer";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import HideIf from "util/HideIf";
import styles from "./rewards.module.scss";

const sections = {
  overview: [
    {
      text: "Earn At Least 1 Point Per $1 Spent",
      image: "rewards-pa",
    },
    {
      text: "Redeem Points on Future Purchases",
      image: "rewards-track",
    },
    {
      text: "Earn Bonus Points on Birthdays and Promotions",
      image: "rewards-bonus-birthday",
    },
    {
      text: "Get Exclusive Offers and Early Sale Access",
      image: "rewards-offers",
    },
  ],
  earn: [
    {
      text: "Make a purchase",
      subtext: "At least 1 point per $1",
      image: "rewards-earn-refer",
    },
    {
      text: "Complete your profile",
      image: "rewards-earn-review",
      points: 250,
    },
    {
      text: "Birthday Month",
      image: "rewards-bonus-birthday",
      points: 500,
    },
  ],
  redeem: [
    {
      text: "Shop and Watch Your Points Grow",
      sub: "Earn at least 1 point per $1 spent, and more during promotions.",
      image: "rewards-coins.webp",
      button: "Start Earning",
      link: "/replacement-parts",
    },
    {
      text: "Shop With Your Points",
      sub: "Use your points to pay for purchases and save cash.",
      image: "rewards-points.png",
      button: "Redeem Now",
      link: "/shopping-cart",
    },
  ],
};

function Rewards(props: any) {
  const { t } = useTranslation("static-pages");
  const { loggedIn } = useSelector((state: RootState) => state.authenticationState);
  const dispatch = useDispatch();
  const { locale, push } = useRouter();
  const faqs = props?.actualResponse?.landingPage?.links?.contentIds?.find((id) => id?.url === `${locale === "en" ? "" : "fr-"}rewards-faq`)?.uiDetails?.content?.faqs || [];

  return (
    <PageContainer fullWidth>
      <div className={styles.rewards_page}>
        <PAImage
          src={getRelativeImagePath("/cms/pubicImages/images/rewards-header.png")}
          alt="Rewards"
          height={300}
          width={300}
          className={styles.rewards_header_image}
        />

        <div
          className={`${styles.rewards_section} ${styles.rewards_intro_section}`}
        >
          <PAText component="h1" className={styles.rewards_main_title} bold>
            {t("AVATAR CLUB")}
          </PAText>
          <PAText component="h3" className={styles.rewards_title}>
            {t("SHOP. EARN POINTS. GET REWARDED.")}
          </PAText>

          <div className={styles.rewards_overview}>
            {sections.overview.map((item) => (
              <div key={item.text} className={styles.rewards_overview_item}>
                <PAImage
                  src={getRelativeImagePath(`/cms/pubicImages/images/${item.image}.png`)}
                  alt={item.text}
                  height={70}
                  width={70}
                />
                <PAText component="p" className="m-t-10">{t(item.text)}</PAText>
              </div>
            ))}
          </div>
        </div>

        <HideIf show={!loggedIn}>
          <div
            className={`${styles.rewards_section} ${styles.join_now_section}`}
          >
            <PAImage
              src={getRelativeImagePath("/cms/pubicImages/images/rewards-partsavatar.png")}
              alt="Join Now"
              height={200}
              width={200}
            />
            <div className={styles.join_now_text}>
              <PAText component="h3" bold className={styles.rewards_title}>
                {t("JOIN NOW, ITS FREE")}
              </PAText>
              <div>
                <PAText className={styles.rewards_subtitle_text}>
                  {t("Get")}
                </PAText>
                <PAText bold className={`${styles.rewards_subtitle_text} m-r-5 m-l-5`}>
                  {t("250 Bonus Points")}
                </PAText>
                <PAText className={styles.rewards_subtitle_text}>
                  {t("When You Sign Up and Complete Your Profile")}
                </PAText>
              </div>

              <div className={styles.join_now_buttons}>
                <PAButton
                  bold
                  className={styles.join_now}
                  buttonType={BUTTON_TYPE.OUTLINE}
                  onClick={() => dispatch(openModal("signup", {}))}
                >
                  {t("Join Now")}
                </PAButton>
                <PAButton
                  bold
                  className={styles.sign_in}
                  onClick={() => dispatch(openModal("login", {}))}
                >
                  {t("Sign In")}
                </PAButton>
              </div>
            </div>
          </div>
        </HideIf>

        <div className={styles.rewards_section}>
          <PAText component="h3" className={styles.rewards_title}>
            {t("HOW TO EARN POINTS")}
          </PAText>
          <div className={styles.earn_points}>
            {sections.earn.map((item) => (
              <div key={item.text} className={styles.earn_points_item}>
                <PAImage
                  src={getRelativeImagePath(`/cms/pubicImages/images/${item.image}.png`)}
                  alt={item.text}
                  height={40}
                  width={40}
                />
                <PAText component="p" className="m-t-10">{t(item.text)}</PAText>

                <PAText component="p" className="m-t-10" bold>
                  {item?.subtext ? t(item.subtext) : t(`+${item.points} pts`)}
                </PAText>
              </div>
            ))}
          </div>
        </div>

        <div className={`${styles.rewards_section} ${styles.rewards_promo_section}`}>
          <div className={styles.promo_text}>
            <PAText bold fontSize={FONT_SIZE[3]}>
              {t("EARN AT LEAST 1 POINT PER $1")}
            </PAText>

            <PAText>
              {t("And even more during our frequent Point Multiplier Events and Category Promotions.")}
            </PAText>
          </div>

          <PAImage
            src={getRelativeImagePath("/cms/pubicImages/images/rewards-banner.png")}
            alt="rewards promo"
            className={styles.promo_image}
            width={300}
            height={300}
          />
        </div>

        <div className={`${styles.rewards_section} ${styles.subscribe_section}`}>
          <i className={`fa fa-lightbulb-o ${styles.lightbulb}`} />
          <PAText bold>
            {t("MAKE SURE YOU ARE SUBSCRIBED TO EMAIL TO RECEIVE EXCLUSIVE PROMOTIONS")}
          </PAText>
        </div>

        <div className={styles.rewards_section}>
          <PAText component="h3">
            {t("FAQs")}
          </PAText>

          <FaqAccordion faqs={faqs} faqColCount={2} />
        </div>

        <div className={styles.rewards_section}>
          <div className={styles.redeem_points}>
            {sections.redeem.map((item) => (
              <div key={item.text} className={styles.redeem_points_item}>
                <PAImage
                  src={getRelativeImagePath(`/cms/pubicImages/images/${item.image}`)}
                  alt={item.text}
                  height={300}
                  width={400}
                  className={styles.redeem_points_item_image}
                />
                <div className={styles.redeem_points_item_text}>
                  <PAText component="p" bold>
                    {t(item.text)}
                  </PAText>
                  <PAText component="p">{t(item.sub)}</PAText>
                  <PAButton className={styles.redeem_button} onClick={() => push(item.link)}>
                    {t(item.button)}
                  </PAButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

export default Rewards;
